import { post } from '@citrite/http';
import { t } from '@citrite/translate';
import { notifyError } from '@citrite/web-ui-component';
import {
	hasFeatureCanary,
	WorkspaceConfiguration,
	workspaceUser as workspaceUserAccessor,
} from '@citrite/workspace-ui-platform';
import { trackAnalyticsEvent, trackEvent } from 'analytics';
import { logError } from 'remoteLogging';
import { v4 } from 'uuid';
import { _aot, AotStrings } from 'App/AotTrace';
import {
	getLocalAppsFromStorage,
	removeLocalApp,
	updateResourceContextWithLocalApps,
} from 'App/LocalSystemApps';
import { getConfigurationService } from 'Configuration';
import { environment } from 'Environment';
import { isBrowserExtensionError } from 'Environment/BrowserExtension/browserExtensionMessageHelper';
import { isChromeAppError } from 'Environment/ChromeApp/ChromeAppMessageHelper';
import { FeatureFlag } from 'Environment/FeatureFlag';
import { clientType, getClientType } from 'Environment/getClientType';
import {
	checkLaunchPreference,
	leaseLaunchForHTML5,
} from 'Environment/Html5/Html5LeaseLaunch';
import { showAppProtectionErrorModal } from 'Environment/launchResource/AppProtectionError';
import { ChromeAppLaunchOptions } from 'Environment/launchResource/chromeApp';
import { handleCWALaunchError } from 'Environment/launchResource/CWALaunchResponseHandler';
import {
	getLatestResourceStatus,
	getLaunchMechanism,
	getTroubleshootFixStatus,
	getTroubleshootFixStatusForLaunchFailure,
	updateMachinePowerStatus,
} from 'Environment/launchResource/launchUtil';
import { isStoreFrontFallbackEnabled } from 'ErrorPage/StoreFrontFallback';
import { GacsChromeAppTransport } from 'GlobalAppConfigService/ChromeAppHtml5';
import { isHTML5ShieldEnabled } from 'LeaseWorkflow';
import { isCitrixBrowserApp } from 'Workspace/AppContext/citrixBrowserResource';
import { fallbackToPreviousClientType } from 'Workspace/BrowserExtension/fallbackToPreviousClientType';
import { createCasPublisher } from 'Workspace/CAS/createCasPublisher';
import { ResourceContext } from 'Workspace/ResourceProvider';
import { ResourceLaunchProgressMap } from 'Workspace/ResourceProvider/Context';
import {
	LaunchPreference,
	localAppResourceType,
	Resource,
} from 'Workspace/ResourceProvider/resourceTypes';
import {
	browserExtensionAnalyticsReporter,
	trackBrowserExtensionError,
} from 'Workspace/TelemetryEvents/browserExtension/createBrowserExtensionAnalyticsReporter';
import { createResourceSessionCasReporter } from 'Workspace/TelemetryEvents/cvadLaunch/createResourceSessionCasReporter';

export interface LaunchOptions {
	isAutoLaunch?: boolean;
	chromeAppOptions?: ChromeAppLaunchOptions;
	fetchIcaParams?: FetchIcaParams;
}

export interface FetchIcaParams {
	isIframeLaunch: boolean;
	launchId: number;
	correlationId: string;
}

export interface LaunchParams {
	resource: Resource;
	resourceContext: ResourceContext;
	workspaceConfiguration: WorkspaceConfiguration;
	launchOptions?: LaunchOptions;
	params?: any;
	targetWindow?: Window;
	troubleshoot?: boolean;
}

interface LaunchProgressParams {
	operationId: string;
	resourceContext: ResourceContext;
	resourceId: string;
	onLaunchCancelled: () => void;
	troubleshoot?: boolean;
	isTroubleshootFixInitiated?: boolean;
}

export enum LaunchStatus {
	CheckingAvailability = 'checking-availability',
	PerformingLaunch = 'performing-launch',
	PoweringOn = 'powering-on',
	Cancelling = 'cancelling',
	Completed = 'completed',
	AwaitingRegistration = 'awaiting-registration',
	Failed = 'failed',
}

export enum CancellationReason {
	UserCancellation = 'user-cancellation',
	NetworkGateway = 'network-gateway',
	NetworkVDA = 'network-vda',
	HDXHandshake = 'hdx-handshake',
	LaunchCancellationOther = 'launch-cancellation-other',
}

/**
 * This function needs to be removed later
 * when the new error handling is implemented.
 * right now we are leaving as it is because this is used to show popups in case of certain errors like protected resources and storefront fallback error
 */
export async function handleLaunchFailures(
	options: LaunchParams,
	workspaceConfiguration: WorkspaceConfiguration,
	error?: Error,
	transactionId?: string
) {
	const shouldUseStoreFrontFallback = isStoreFrontFallbackEnabled(workspaceConfiguration);
	let canUpdateLaunchProgressContext = true;
	const shouldShowAppProtectionLaunchFailureRequireBrowserExtensionModal =
		(hasFeatureCanary(
			workspaceConfiguration,
			FeatureFlag.CombinedAppProtectionChecking
		) &&
			workspaceConfiguration.appProtection?.enabled?.toLowerCase() ===
				'requireswebextension') ||
		!hasFeatureCanary(workspaceConfiguration, FeatureFlag.CombinedAppProtectionChecking);

	switch (error?.message) {
		case 'CouldNotConnectToWorkstation':
		case 'NoMoreActiveSessions':
		case 'UnavailableDesktop':
		case 'WorkstationInMaintenance':
			// These errors don't need special handling or message modification
			break;
		case 'UnavailableAppProtectedDesktop':
			//this will be received only in case of app protected resource launch failure.
			if (hasFeatureCanary(workspaceConfiguration, FeatureFlag.BrowserAppProtection)) {
				//if client type is html5, user is shown with modal for referring to help page
				if (getClientType() === clientType.html5) {
					showAppProtectionErrorModal({
						title: t('Workspace:app_Protection_launch_failures.Session_Failed_To_Launch'),
						message: t('Workspace:app_Protection_launch_failures.Refer_Help_Page'),
						linkMessage: t('Workspace:app_Protection_launch_failures.Help'),
						linkUrl: 'https://support.citrix.com/article/CTX327882',
					});
				} else if (shouldShowAppProtectionLaunchFailureRequireBrowserExtensionModal) {
					//if client type is native , then user is shown with modal to use browser extension.
					showAppProtectionErrorModal({
						title: t('Workspace:app_Protection_launch_failures.Web_Extension_Required'),
						message: t('Workspace:app_Protection_launch_failures.Use_Browser_Extension'),
						linkMessage: t('Workspace:app_Protection_launch_failures.Help'),
						linkUrl: 'https://support.citrix.com/article/CTX327881',
					});
				}
			} else {
				error.message = 'UnavailableDesktop';
			}
			break;
		case 'GeneralAppLaunchError':
			if (shouldUseStoreFrontFallback) {
				await showStoreFrontFallbackError(workspaceConfiguration);
				canUpdateLaunchProgressContext = false;
			}
			break;
		default:
			logError(error, {
				tags: {
					feature: 'apps-and-desktops',
				},
			});
			const isInspectConnectionEnabled = hasFeatureCanary(
				workspaceConfiguration,
				FeatureFlag.EnableInspectConnection
			);
			if (shouldUseStoreFrontFallback) {
				await showStoreFrontFallbackError(workspaceConfiguration);
				canUpdateLaunchProgressContext = false;
			}
			if (!isInspectConnectionEnabled) {
				error.message = options.launchOptions?.isAutoLaunch
					? 'ResourceAutoLaunchFailure'
					: 'DefaultLaunchError';
			}
			if (!error?.message) {
				error.message = 'DefaultLaunchError';
			}
	}
	if (canUpdateLaunchProgressContext) {
		const troubleshootFixStatus = getTroubleshootFixStatusForLaunchFailure(
			options.params?.isTroubleshootFixInitiated,
			options.resource
		);
		options.resourceContext.updateSession(state => ({
			launchProgress: {
				...state.launchProgress,
				[transactionId]: {
					...state.launchProgress[transactionId],
					resourceId: options.resource.id,
					status: LaunchStatus.Failed,
					error: error?.message,
					timeStamp: Date.now(),
					troubleshoot: options.troubleshoot,
					troubleshootFixStatus,
				},
			},
		}));
	}
}

async function showStoreFrontFallbackError(workspaceConfig: WorkspaceConfiguration) {
	const { showStoreFrontFallbackErrorModal } = await import('./StorefrontFallbackError');
	showStoreFrontFallbackErrorModal(workspaceConfig.userInterface.fallbackConfiguration);
}

const notifyLaunchCancellation = (
	launchCancellationToken: string,
	cancellationUrl: string
) => {
	return post<any>(cancellationUrl, {
		LaunchCancellationToken: launchCancellationToken,
		CancellationReason: CancellationReason.UserCancellation,
		CancellationReasonText: 'User cancelled the launch from Workspace UI',
	}).catch(error => logError(error));
};

const generateLaunchProgressHandler = (launchProgressParams: LaunchProgressParams) => {
	const {
		operationId,
		resourceContext,
		resourceId,
		onLaunchCancelled,
		troubleshoot,
		isTroubleshootFixInitiated,
	} = launchProgressParams;
	const cancellationUrl = resourceContext.resources.find(
		resource => resource.id === resourceId
	)?.cancellaunch;
	const launchCancellationToken = cancellationUrl ? v4() : undefined;
	let isCanceled = false;
	const onCancel = () => {
		if (cancellationUrl) {
			notifyLaunchCancellation(launchCancellationToken, cancellationUrl);
		}
		isCanceled = true;
		onLaunchCancelled();
	};
	const validateLaunchNextStep = (status: LaunchStatus) => {
		if (isCanceled) {
			return false;
		}
		const troubleshootFixStatus = getTroubleshootFixStatus(
			isTroubleshootFixInitiated,
			status
		);
		resourceContext.updateSession(state => ({
			launchProgress: {
				...state.launchProgress,
				[operationId]: {
					status,
					onCancel,
					resourceId,
					troubleshoot,
					timeStamp: Date.now(),
					troubleshootFixStatus,
				},
			},
		}));
		return true;
	};
	const launchProgressHandler = {
		validateLaunchNextStep,
		launchCancellationToken,
	};
	return launchProgressHandler;
};

export async function launchResource(params: LaunchParams) {
	const resourceLaunchClickTime = Date.now() / 1000;
	const { resource, workspaceConfiguration, resourceContext, ...launchParams } = params;
	const launchMechanism = getLaunchMechanism(workspaceConfiguration);
	launchParams.params = {
		LaunchMechanism: launchMechanism,
		RLCT: resourceLaunchClickTime,
		...launchParams.params,
	};
	if (resourceContext.isInProgress(resource.id)) {
		return Promise.resolve();
	}
	resourceContext.updateSession(state => ({
		launchInProgressIds: [...state.launchInProgressIds, resource.id],
	}));

	if (isCitrixBrowserApp(resource)) {
		await environment.launchCitrixWorkspaceBrowserApp({ resourceID: resource.id });
		postLaunchStep(resourceContext, resource.id, true);
	} else if (resource.type === localAppResourceType) {
		try {
			_aot.info(
				AotStrings.launch.category,
				AotStrings.launch.localAppLaunch,
				resource.name
			);
			await environment.launchLocalApp(resource, {
				path: resource.launchurl,
			});
		} catch (error) {
			_aot.error(
				AotStrings.launch.category,
				AotStrings.launch.localAppLaunchFailed,
				error,
				resource.name
			);
			if (error === 'ErrorFileNotFound') {
				await removeLocalApp(resource.id);
				const localAppsAfterRemove = await getLocalAppsFromStorage();
				updateResourceContextWithLocalApps(resourceContext, localAppsAfterRemove);
				notifyError(t('Workspace:localapp_launch_notfound'));
			}
		} finally {
			trackEvent('LocalAppLaunch');
			//Local apps launch immediately, but a short loading indicator provides more tactile feedback for app launch,
			//aligns with CVAD behavior, and helps prevent accidental double clicks
			await new Promise(resolve => {
				setTimeout(resolve, 1000);
			});
			resourceContext.updateSession(state => ({
				launchInProgressIds: state.launchInProgressIds.filter(id => id !== resource.id),
			}));
		}
	} else {
		_aot.info(AotStrings.launch.category, AotStrings.launch.icaAppLaunch, resource.name);
		const operationId = v4();

		const workspaceUser = workspaceUserAccessor.get();
		const eventPublisher = createCasPublisher(
			workspaceConfiguration,
			workspaceUser.userDetails
		);
		const {
			getStartEventPayload,
			getEndEventPayload,
			getErrorEventPayload,
			getCancelEventPayload,
		} = createResourceSessionCasReporter(
			operationId,
			resource.id,
			workspaceUser.userDetails.userId
		);

		const launchProgressHandler = generateLaunchProgressHandler({
			operationId,
			resourceContext,
			resourceId: resource.id,
			onLaunchCancelled: () => eventPublisher.publishEvent(getCancelEventPayload()),
			troubleshoot: launchParams.troubleshoot,
			isTroubleshootFixInitiated: launchParams.params?.isTroubleshootFixInitiated,
		});
		const { validateLaunchNextStep } = launchProgressHandler;
		const preferLeaseLaunch = resourceContext.preferLeaseLaunchIds.includes(resource.id);
		if (
			getClientType() === clientType.browserextension &&
			(await resourceContext.shouldBrowserExtensionLaunchResource(resource)) === true
		) {
			try {
				await resourceContext.tryLaunchViaBrowserExtension({
					...launchParams,
					resource,
					workspaceConfiguration,
					preferLeaseLaunch,
					launchProgressHandler,
				});
				validateLaunchNextStep(LaunchStatus.Completed);
				postLaunchStep(resourceContext, resource.id, true);
				return;
			} catch (error) {
				_aot.error(
					AotStrings.launch.category,
					AotStrings.launch.icaAppLaunchFailedWithBE,
					error,
					resource.name
				);
				if (isBrowserExtensionError(error)) {
					logError(error, {
						tags: {
							feature: 'browserExtension',
							status: error['status'],
						},
					});

					if (!fallbackToPreviousClientType()) {
						// If there is no previous client type, we set it to native
						logError(new Error('No previous client type for fallback.'), {
							additionalContext: {
								error,
							},
							tags: {
								feature: 'browserExtension',
							},
						});
					}
					trackBrowserExtensionError(error);
				} else {
					trackBrowserExtensionError(error);
					return handleLaunchFailures(
						params,
						workspaceConfiguration,
						error,
						operationId
					).then(() => postLaunchStep(resourceContext, resource.id, false));
				}
			} finally {
				trackAnalyticsEvent(
					browserExtensionAnalyticsReporter.getBrowserExtensionAppLaunchEvent()
				);
			}
		} else if (
			getClientType() === clientType.chromeapp &&
			(await resourceContext.shouldChromeAppLaunchResource(resource)) === true
		) {
			try {
				GacsChromeAppTransport.getInstance().validateAndFetchAppConfiguration();
				await resourceContext.tryLaunchViaChromeApp({
					...launchParams,
					resource,
					workspaceConfiguration,
					preferLeaseLaunch,
					operationId,
					launchProgressHandler,
				});
				validateLaunchNextStep(LaunchStatus.Completed);
				postLaunchStep(resourceContext, resource.id, true);
				return;
			} catch (error) {
				_aot.error(
					AotStrings.launch.category,
					AotStrings.launch.icaAppLaunchFailedWithChromeApp,
					error,
					resource.name
				);
				if (isChromeAppError(error)) {
					logError(error, {
						tags: {
							feature: 'chromeapp',
							status: error['status'],
						},
					});

					if (!fallbackToPreviousClientType()) {
						// If there is no previous client type, we set it to native
						logError(new Error('No previous client type for fallback.'), {
							additionalContext: {
								error,
							},
							tags: {
								feature: 'chromeapp',
							},
						});
					}
				} else {
					return handleLaunchFailures(
						params,
						workspaceConfiguration,
						error,
						operationId
					).then(() => postLaunchStep(resourceContext, resource.id, false));
				}
			} finally {
				trackEvent('ChromeAppLaunch');
			}
		} else if (isHTML5ShieldEnabled(workspaceConfiguration)) {
			if (
				!environment.citrixCloudConnected ||
				checkLaunchPreference(resource, LaunchPreference.LeaseOnly)
			) {
				try {
					await leaseLaunchForHTML5({
						...launchParams,
						resource,
						workspaceConfiguration,
						preferLeaseLaunch,
						launchProgressHandler,
					});
					validateLaunchNextStep(LaunchStatus.Completed);
					postLaunchStep(resourceContext, resource.id, true);
					return;
				} catch (error) {
					_aot.error(
						AotStrings.launch.category,
						AotStrings.launch.shieldLaunchFailed,
						error,
						resource.name
					);
					return handleLaunchFailures(params, workspaceConfiguration, error).then(() =>
						postLaunchStep(resourceContext, resource.id, false)
					);
				} finally {
					trackEvent('Html5ShieldLaunch');
				}
			}
		}

		eventPublisher.publishEvent(getStartEventPayload());
		return environment
			.launchResource({
				...launchParams,
				resource,
				workspaceConfiguration,
				preferLeaseLaunch,
				operationId,
				launchProgressHandler,
				...(resourceContext.citrixBrowserResourceID && {
					citrixBrowserResourceID: resourceContext.citrixBrowserResourceID,
				}),
			})
			.then(handleCWALaunchError)
			.then(() => {
				const isValid = launchProgressHandler.validateLaunchNextStep(
					LaunchStatus.Completed
				);
				if (isValid) {
					eventPublisher.publishEvent(getEndEventPayload());
				}
				return true;
			})
			.catch(async error => {
				_aot.error(
					AotStrings.launch.category,
					AotStrings.launch.icaAppLaunchFailed,
					error,
					resource.name
				);

				eventPublisher.publishEvent(getErrorEventPayload());
				handleLaunchFailures(params, workspaceConfiguration, error, operationId);
				return false;
			})
			.then(isLaunchSuccessful => {
				postLaunchStep(resourceContext, resource.id, isLaunchSuccessful);
			});
	}
}

function postLaunchStep(
	context: ResourceContext,
	resourceId: string,
	isLaunchSuccessful: boolean
) {
	const launchConfig = getConfigurationService().launchConfiguration;
	const postLaunchDelayInMs = launchConfig.getPostLaunchDelayForHybridLaunchesInMs(
		isLaunchSuccessful,
		environment.isNative
	);

	const executeDelayedPostLaunchStep = () =>
		delayedPostLaunchStep(context, resourceId, isLaunchSuccessful);

	if (postLaunchDelayInMs > 0) {
		setTimeout(executeDelayedPostLaunchStep, postLaunchDelayInMs);
	} else {
		executeDelayedPostLaunchStep();
	}
}

function delayedPostLaunchStep(
	context: ResourceContext,
	resourceId: string,
	isLaunchSuccessful: boolean
) {
	if (!IS_ON_PREM) {
		context.makeResourceMostRecent(resourceId);
	}
	// This is needed until we have the launch failures captured in LaunchProgressHandler
	// Today this postLaunchStep is called for both success and failure launch cases
	// We need to updated the machine power status only if the launch is successful
	// This temporary code will be removed once we have the launch failures captured in LaunchProgressHandler
	// The same will be taken care with the ticket - https://issues.citrite.net/browse/WSUI-10511
	isLaunchSuccessful && updateMachinePowerStatus(resourceId);
	context.updateSession(state => {
		return {
			launchInProgressIds: state.launchInProgressIds.filter(id => id !== resourceId),
			launchProgress: Object.keys(state.launchProgress).reduce(
				(launchProgress, operationId) => {
					const progress = state.launchProgress[operationId];
					if (
						progress.resourceId !== resourceId ||
						progress.status === LaunchStatus.Failed
					) {
						launchProgress[operationId] = state.launchProgress[operationId];
					}
					return launchProgress;
				},
				{} as ResourceLaunchProgressMap
			),
			resourceLatestStatus: getLatestResourceStatus(
				state.launchProgress,
				context.resourceLatestStatus
			),
		};
	});
}

// allow-unused-export to be called later
export function isLaunchResourceWithIcaSupported(): boolean {
	return typeof environment?.launchResourceWithIca === 'function';
}
